import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { Deal } from '../types/deal'
import type { Invoice } from '../types/invoice'
import { useCompanyOptionsQuery } from '../hooks/useCompanyOptionsQuery'
import { useInvoicesQuery } from '../hooks/useInvoicesQuery'
import { useDealQuery } from '../hooks/useDealQuery'
import { useSendInvoiceMutation } from '../hooks/useSendInvoiceMutation'
import { useModal } from '../state/ModalProvider'
import { formatCents } from '../utils/formatCents'

import { DEFAULT_MESSAGE, dealCcFor, dealEmailFor, joinCcList, parseCcList } from './invoiceEmail'

interface Props {
  dealId: string
  invoiceId: string
}

interface SendForm {
  to: string
  cc: string
  subject: string
  message: string
}

function buildPrefill(
  invoice: Invoice | null | undefined,
  deal: Deal | null | undefined,
  companyName: string,
): SendForm {
  if (invoice?.status === 'Draft' && invoice.draftEmail) {
    return {
      to: invoice.draftEmail.to,
      cc: joinCcList(invoice.draftEmail.cc),
      subject: invoice.draftEmail.subject,
      message: invoice.draftEmail.message,
    }
  }
  return {
    to: dealEmailFor(deal, companyName),
    cc: joinCcList(dealCcFor(deal)),
    subject: invoice ? `Invoice ${invoice.invoiceNumber} from Calyx` : '',
    message: DEFAULT_MESSAGE,
  }
}

function formatTotalForChip(invoice: Invoice | null | undefined): string {
  if (!invoice) return ''
  return formatCents(invoice.totalCents)
}

export const SendInvoiceModal: React.FunctionComponent<Props> = (props) => {
  const { dealId, invoiceId } = props
  const { close, openInvoicePreview } = useModal()
  const dealQuery = useDealQuery(dealId)
  const invoicesQuery = useInvoicesQuery(dealId)
  const sendMutation = useSendInvoiceMutation()

  const companyOptionsQuery = useCompanyOptionsQuery()
  const deal = dealQuery.data ?? null
  const invoices = invoicesQuery.data ?? []
  const invoice = useMemo(
    () => invoices.find((entry) => entry.id === invoiceId) ?? null,
    [invoices, invoiceId],
  )
  const companyName = useMemo(() => {
    if (!deal) return ''
    return (
      companyOptionsQuery.data?.find((option) => option.id === deal.companyId)?.name ??
      deal.companyId
    )
  }, [companyOptionsQuery.data, deal])

  // Detect Mongo ObjectId-shaped companyId (24 hex chars). Real backend uses
  // these and the prefill must wait for the catalog to resolve them into a
  // human-readable name (otherwise email slug ends up as the UUID itself).
  // Mock-mode companyIds are human-readable strings (e.g. 'Tyson'); they fail
  // this test so the gate opens immediately.
  const companyIdLooksLikeUuid = !!deal && /^[a-f0-9]{24}$/i.test(deal.companyId)
  const isCatalogResolved =
    companyOptionsQuery.isSuccess &&
    (!companyIdLooksLikeUuid || companyName !== deal?.companyId)

  // Idempotency-Key only flips when the modal is reopened, so retries within
  // the same modal lifetime collapse on the backend.
  const idempotencyKey = useMemo(
    () =>
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    [],
  )

  const [form, setForm] = useState<SendForm>(() => buildPrefill(invoice, deal, companyName))
  const [didPrefill, setDidPrefill] = useState(false)

  useEffect(() => {
    if (didPrefill) return
    if (!deal || !invoice) return
    if (invoice.status !== 'Draft' && !isCatalogResolved) return
    setForm(buildPrefill(invoice, deal, companyName))
    setDidPrefill(true)
  }, [didPrefill, deal, invoice, companyName, isCatalogResolved])

  // Hard guard: once the send mutation kicks off we lock the action buttons
  // until the modal closes (success path) so a double-click cannot fire two
  // sends.
  const isSubmitting = sendMutation.isLoading

  // Race watcher for the 409-in-flight case (another concurrent send wins
  // while this modal is still open). Order-sensitive — verify these hold
  // when upgrading react-query:
  //
  // 1. useSendInvoiceMutation.onMutate optimistically writes `sentAt` into
  //    the cached invoice; this effect would close the modal prematurely if
  //    not gated.
  // 2. `isSubmitting` (== sendMutation.isLoading) gates the close branch so
  //    the current modal's own optimistic write is excluded. The submitter
  //    closes itself via `onSuccess`, not via this watcher.
  // 3. react-query v4 lifecycle: on error the mutation runs onError first,
  //    then flips isLoading=false; invalidate-driven refetch then restores
  //    the true server `sentAt`. So by the time this effect sees
  //    `invoice.sentAt` after `!isSubmitting`, the value is server truth,
  //    not a stale optimistic write.
  // 4. If react-query is upgraded (especially to v5+), re-validate that
  //    onError → isLoading=false → refetch ordering still holds before
  //    trusting this gate.
  const initialSentAtRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!invoice) return
    if (initialSentAtRef.current === undefined) {
      initialSentAtRef.current = invoice.sentAt
      return
    }
    if (isSubmitting) return
    if (!initialSentAtRef.current && invoice.sentAt) close()
  }, [invoice, close, isSubmitting])

  if (!deal || !invoice) {
    return (
      <div className="send-inv-overlay active" onClick={close}>
        <div className="send-inv-dialog" onClick={(event) => event.stopPropagation()}>
          <div className="send-inv-header">
            <h3>Send invoice</h3>
            <div className="sub">Loading invoice…</div>
          </div>
          <div className="send-inv-body">
            <div className="send-inv-summary">
              <span className="inv-num">{invoiceId}</span>
            </div>
          </div>
          <div className="send-inv-footer" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  const totalChip = formatTotalForChip(invoice)

  function handleSend(): void {
    if (isSubmitting) return
    const trimmed = {
      to: form.to.trim(),
      cc: parseCcList(form.cc),
      subject: form.subject.trim(),
      message: form.message,
    }
    if (!trimmed.to) return
    sendMutation.mutate(
      { dealId, invoiceId, body: trimmed, idempotencyKey },
      {
        onSuccess: () => {
          close()
        },
      },
    )
  }

  return (
    <div className="send-inv-overlay active" onClick={close}>
      <div className="send-inv-dialog" onClick={(event) => event.stopPropagation()}>
        <div className="send-inv-header">
          <h3>Send invoice</h3>
          <div className="sub">A PDF copy will be attached automatically.</div>
        </div>
        <div className="send-inv-body">
          <div className="send-inv-summary">
            <span className="inv-num">{invoice.invoiceNumber}</span>
            <span className="dot">·</span>
            <span>{companyName}</span>
            {totalChip ? (
              <>
                <span className="dot">·</span>
                <span className="total">{totalChip}</span>
              </>
            ) : null}
          </div>
          <div className="fg">
            <label>
              To <span style={{ color: '#ff3b30' }}>*</span>
            </label>
            <input
              type="email"
              placeholder="billing@customer.com"
              value={form.to}
              onChange={(event) => setForm((current) => ({ ...current, to: event.target.value }))}
            />
          </div>
          <div className="fg">
            <label>
              CC <span style={{ color: '#6e6e73', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text"
              placeholder="Separate multiple addresses with a comma"
              value={form.cc}
              onChange={(event) => setForm((current) => ({ ...current, cc: event.target.value }))}
            />
          </div>
          <div className="fg">
            <label>Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(event) =>
                setForm((current) => ({ ...current, subject: event.target.value }))
              }
            />
          </div>
          <div className="fg">
            <label>Message</label>
            <textarea
              value={form.message}
              onChange={(event) =>
                setForm((current) => ({ ...current, message: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="send-inv-footer" style={{ justifyContent: 'space-between' }}>
          <button
            type="button"
            className="btn-link-preview"
            title="Open PDF preview"
            onClick={() => openInvoicePreview(dealId, invoiceId)}
          >
            <svg
              viewBox="0 0 24 24"
              width={14}
              height={14}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx={12} cy={12} r={3} />
            </svg>
            Preview PDF
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-ghost" onClick={close} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!form.to.trim() || isSubmitting}
              onClick={handleSend}
            >
              <svg
                viewBox="0 0 24 24"
                width={14}
                height={14}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 2 11 13" />
                <path d="M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
              {sendMutation.isLoading ? 'Sending…' : 'Send Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
